// src/components/features/content/ContentDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Card,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Send as PublishIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  BarChart as AnalyticsIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

// Import UI components
import { EntityStatusBadge } from '../../core';
import { MagicLoader } from '../../../design-system';
import {
  EmptyState,
  Breadcrumbs,
  ActionsMenu,
  ConfirmationDialog,
  ContentTypeIcon,
  PlatformChip,
  AssetPreview,
  TabPanel,
  CommentSection,
  ApprovalWorkflowSection,
  MetricsSection,
  // Styled components removed - using MUI components directly
} from '../../ui';

// Import hooks
import useContentStore from '../../../stores/contentStore';
import useBrandStore from '../../../stores/brandStore';
import useNotificationStore from '../../../stores/notificationStore';
import useAuthStore from '../../../stores/authStore';
import { format } from 'date-fns';

// Content Detail Component
const ContentDetail = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentContent,
    isLoading,
    criticalError,
    fetchContentById,
    deleteContent,
    approveContent,
    rejectContent,
    publishContent,
    addContentComment,
    clearCurrentContent,
    clearCriticalError
  } = useContentStore();
  
  const { fetchBrand } = useBrandStore();
  const { showSuccess, showError } = useNotificationStore();
  const { user, hasPermission } = useAuthStore();
  
  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [brand, setBrand] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null
  });
  
  // Check permissions
  const canUpdate = hasPermission('content', 'update');
  const canDelete = hasPermission('content', 'delete');
  const canApprove = hasPermission('approvals', 'update');
  
  // Fetch content and brand on mount
  useEffect(() => {
    loadContent();
    
    return () => {
      clearCurrentContent();
    };
  }, [id]);
  
  // Load brand when content is loaded
  useEffect(() => {
    if (currentContent?.brandId) {
      loadBrand();
    }
  }, [currentContent]);
  
  // Load content
  const loadContent = async () => {
    try {
      await fetchContentById(id);
    } catch (error) {
      showError(error.message);
    }
  };
  
  // Load brand
  const loadBrand = async () => {
    try {
      const brandId = typeof currentContent.brandId === 'object' 
        ? currentContent.brandId._id 
        : currentContent.brandId;
        
      const brandData = await fetchBrand(brandId);
      setBrand(brandData);
    } catch (error) {
      console.log('Failed to load brand:', error);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Format date 
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };
  
  // Action menu handlers
  const handleActionsOpen = (event) => {
    setActionAnchorEl(event.currentTarget);
  };
  
  const handleActionsClose = () => {
    setActionAnchorEl(null);
  };
  
  // Show confirmation dialog
  const showConfirmation = (title, message, action) => {
    setConfirmationDialog({
      open: true,
      title,
      message,
      action
    });
  };
  
  // Close confirmation dialog
  const closeConfirmation = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false
    });
  };
  
  // Handle delete content
  const handleDeleteContent = async () => {
    try {
      await deleteContent(id);
      showSuccess('Content deleted successfully');
      closeConfirmation();
      navigate('/content');
    } catch (error) {
      showError(`Failed to delete content: ${error.message}`);
      closeConfirmation();
    }
  };
  
  // Handle approve content
  const handleApproveContent = async () => {
    try {
      await approveContent(id);
      showSuccess('Content approved successfully');
      closeConfirmation();
      loadContent();
    } catch (error) {
      showError(`Failed to approve content: ${error.message}`);
      closeConfirmation();
    }
  };
  
  // Handle publish content
  const handlePublishContent = async () => {
    try {
      await publishContent(id);
      showSuccess('Content published successfully');
      closeConfirmation();
      loadContent();
    } catch (error) {
      showError(`Failed to publish content: ${error.message}`);
      closeConfirmation();
    }
  };
  
  // Get brand name
  const getBrandName = () => {
    if (typeof currentContent?.brandId === 'object') {
      return currentContent.brandId.name;
    }
    
    if (brand) {
      return brand.name;
    }
    
    return 'Loading...';
  };
  
  // Define actions for the menu
  const getActions = () => {
    const actions = [];
    const dangerActions = [];
    
    if (canUpdate) {
      actions.push({
        label: 'Edit',
        icon: <EditIcon fontSize="small" />,
        onClick: () => navigate(`/content/${id}/edit`)
      });
    }
    
    if (currentContent.status !== 'published' && canUpdate) {
      actions.push({
        label: currentContent.status === 'scheduled' ? 'Reschedule' : 'Schedule',
        icon: <ScheduleIcon fontSize="small" />,
        onClick: () => navigate(`/content/${id}/schedule`)
      });
    }
    
    if (currentContent.status === 'scheduled' && canUpdate) {
      actions.push({
        label: 'Publish Now',
        icon: <PublishIcon fontSize="small" />,
        onClick: () => showConfirmation(
          'Publish Content',
          'Are you sure you want to publish this content now? This will make it visible to your audience immediately.',
          handlePublishContent
        )
      });
    }
    
    if (currentContent.status === 'pendingApproval' && canApprove) {
      actions.push({
        label: 'Approve',
        icon: <ApproveIcon fontSize="small" color="success" />,
        onClick: () => showConfirmation(
          'Approve Content',
          'Are you sure you want to approve this content?',
          handleApproveContent
        )
      });
      
      actions.push({
        label: 'Reject',
        icon: <RejectIcon fontSize="small" color="error" />,
        onClick: () => navigate(`/content/${id}/reject`)
      });
    }
    
    actions.push({
      label: 'Analytics',
      icon: <AnalyticsIcon fontSize="small" />,
      onClick: () => navigate(`/content/${id}/analytics`)
    });
    
    if (canDelete) {
      dangerActions.push({
        label: 'Delete',
        icon: <DeleteIcon fontSize="small" color="error" />,
        onClick: () => showConfirmation(
          'Delete Content',
          'Are you sure you want to delete this content? This action cannot be undone.',
          handleDeleteContent
        )
      });
    }
    
    return { actions, dangerActions };
  };
  
  // Handler for adding comments
  const handleAddComment = async (comment) => {
    try {
      await addContentComment(id, comment);
      showSuccess('Comment added successfully');
      loadContent();
    } catch (error) {
      showError(`Failed to add comment: ${error.message}`);
    }
  };
  
  // Loading state
  if (isLoading && !currentContent) {
    return (
      <Box sx={{ justifyContent: 'center', height: '50vh' }}>
        <MagicLoader variant="mystical" size="medium" message="Loading content details..." />
      </Box>
    );
  }
  
  // Error state
  if (criticalError && !currentContent) {
    return (
      <EmptyState
        title="Error Loading Content"
        description={criticalError}
        icon={<RejectIcon color="error" fontSize="large" />}
        action={
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/content')}
          >
            Back to Content
          </Button>
        }
        magical={undefined}
      />
    );
  }
  
  // No content found
  if (!currentContent) {
    return (
      <EmptyState
        title="Content Not Found"
        description="The content you are looking for could not be found."
        icon={<CancelIcon fontSize="large" />}
        action={
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/content')}
          >
            Back to Content
          </Button>
        }
        magical={undefined}
      />
    );
  }
  
  // Get the actions and danger actions for the menu
  const { actions, dangerActions } = getActions();
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        current={currentContent.title}
        parentLink="/content"
        parentLabel="Content"
      />
      
      {/* Header */}
      <Card
        sx={{ 
          mb: 4,
          borderTop: `4px solid ${theme.palette[currentContent.status === 'published'
            ? 'success'
            : currentContent.status === 'scheduled'
              ? 'info'
              : currentContent.status === 'pendingApproval'
                ? 'warning'
                : currentContent.status === 'rejected'
                  ? 'error'
                  : 'grey'
          ].main}`
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Title and Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ContentTypeIcon type={currentContent.type} size="medium" />
                <Typography variant="h5" sx={{ ml: 1 }}>
                  {currentContent.title}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <EntityStatusBadge status={currentContent.status} entityType="content" />
                
                {currentContent.language && (
                  <Chip
                    label={currentContent.language === 'bilingual'
                      ? 'English & Arabic'
                      : currentContent.language === 'ar'
                        ? 'Arabic'
                        : 'English'
                    }
                    size="small"
                  />
                )}

                <Chip
                  label={getBrandName()}
                  size="small"
                  icon={<BusinessIcon fontSize="small" />}
                  color="primary"
                />

                {currentContent.campaignId && (
                  <Chip
                    label={typeof currentContent.campaignId === 'object'
                      ? currentContent.campaignId.name
                      : 'Campaign'
                    }
                    size="small"
                    icon={<CampaignIcon fontSize="small" />}
                    color="secondary"
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => navigate('/content')}
              >
                Back
              </Button>

              <IconButton onClick={handleActionsOpen}>
                <MoreVertIcon />
              </IconButton>

              {/* Actions Menu */}
              <ActionsMenu
                anchorEl={actionAnchorEl}
                open={Boolean(actionAnchorEl)}
                onClose={handleActionsClose}
                actions={actions}
                dangerActions={dangerActions}
              />
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Content Details */}
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="h6" gutterBottom>Content Details</Typography>
            
            {/* Content Text */}
            {currentContent.text ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  {currentContent.text}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  No content text has been added yet.
                </Typography>
              </Box>
            )}
            
            {/* Hashtags */}
            {currentContent.hashtags && currentContent.hashtags.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {currentContent.hashtags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={`#${tag}`}
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  No hashtags added
                </Typography>
              </Box>
            )}
            
            {/* Status Info */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Metadata</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Created</Typography>
                <Typography variant="body2">{currentContent.createdAt ? new Date(currentContent.createdAt).toLocaleDateString() : 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                <Typography variant="body2">{currentContent.updatedAt ? new Date(currentContent.updatedAt).toLocaleDateString() : 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <EntityStatusBadge status={currentContent.status} entityType="content" size="small" />
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Version</Typography>
                <Typography variant="body2">v{currentContent.version?.number || '1.0'}</Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Platform Distribution */}
          {currentContent.distributions && currentContent.distributions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Distribution</Typography>
              <Grid container spacing={2}>
                {currentContent.distributions.map((dist, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        p: 2,
                        height: '100%'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <PlatformChip
                          platform={dist.platform}
                          variant="gradient"
                          size="small"
                        />
                        <Chip
                          size="small"
                          label={dist.status?.charAt(0)?.toUpperCase() + dist.status?.slice(1)}
                          color={dist.status === 'published'
                            ? 'success'
                            : dist.status === 'scheduled'
                              ? 'info'
                              : 'default'
                          }
                        />
                      </Box>

                      <Box>
                        {dist?.scheduledTime && (
                          <Box>
                            <Typography variant="body2">Scheduled:</Typography>
                            <Typography variant="body2">{dist.scheduledTime ? new Date(dist.scheduledTime).toLocaleDateString() : 'N/A'}</Typography>
                          </Box>
                        )}

                        {dist?.publishedTime && (
                          <Box>
                            <Typography variant="body2">Published:</Typography>
                            <Typography variant="body2">{dist.publishedTime ? new Date(dist.publishedTime).toLocaleDateString() : 'N/A'}</Typography>
                          </Box>
                        )}

                        {dist?.publishingResponse?.postUrl && (
                          <Button
                            size="small"
                            variant="text"
                            fullWidth
                            href={dist.publishingResponse.postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mt: 1 }}
                          >
                            View Post
                          </Button>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Assets */}
          {currentContent.assets && currentContent.assets.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Assets</Typography>
              <Grid container spacing={2}>
                {currentContent.assets.map((asset, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <AssetPreview asset={asset} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {currentContent.references && currentContent.references.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>References</Typography>
              <Grid container spacing={2}>
                {currentContent.references.map((reference, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <AssetPreview asset={reference} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Card>

      {/* Tabs */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="content tabs"
            sx={{
              '& .MuiTab-root': {
                py: 1.5,
                color: theme.palette.mode === 'dark'
                  ? theme.palette.common.white
                  : theme.palette.common.black,
                '&.Mui-selected': {
                  color: theme.palette.secondary.main,
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.secondary.main,
                height: 3,
                borderRadius: '2px 2px 0 0',
                boxShadow: `0 0 8px ${theme.palette.secondary.main}`
              }
            }}
          >
            <Tab label="Comments" />
            <Tab label="Approval Workflow" />
            <Tab label="Metrics" />
          </Tabs>
        </Box>
        
        {/* Comments Tab */}
        <TabPanel value={tabValue} index={0}>
          <CommentSection 
            comments={currentContent.comments} 
            onAddComment={handleAddComment}
            currentUserId={user?._id}
          />
        </TabPanel>
        
        {/* Approval Workflow Tab */}
        <TabPanel value={tabValue} index={1}>
          <ApprovalWorkflowSection
            workflow={currentContent.approvalWorkflow}
            canApprove={canApprove}
            onSetupWorkflow={() => navigate(`/content/${id}/workflow`)}
            contentId={id}
          />
        </TabPanel>
        
        {/* Metrics Tab */}
        <TabPanel value={tabValue} index={2}>
          <MetricsSection
            metrics={currentContent.metrics}
            status={currentContent.status}
            contentId={id}
            onViewAnalytics={() => navigate(`/content/${id}/analytics`)}
          />
        </TabPanel>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        onCancel={closeConfirmation}
        onConfirm={() => {
          if (confirmationDialog.action) {
            confirmationDialog.action();
          }
        }}
        confirmColor={confirmationDialog.title.includes('Delete') ? 'error' : 'primary'}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default ContentDetail;